import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, State, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { AbstractEffects } from 'app/core/store/AbstractEffects';
import { concat, from, Observable, of } from 'rxjs';
import { AzureMonitoringService } from '../../../services/AzureMonitoringService';
import { UserRestService } from '../../../services/rest/admin/user.rest.service';
import { CustomersRestService } from '../../../services/rest/customers.rest.service';
import * as actions from './users.actions';
import {
  ClientUserBulkUploadAction,
  ClientUserCreateAction,
  ClientUserInviteAction,
  ClientUserLoadAction,
  ClientUserLoadShipTosAction,
  ClientUserLoadSoldTosAction,
  ClientUsersLoadAction,
  ClientUserUpdateAction,
  EpsUserCreateAction,
  EpsUserInviteAction,
  EpsUserLoadAction,
  EpsUsersLoadAction,
  EpsUserUpdateAction,
  UserReInviteAction,
  UserResetPasswordAction,
} from './users.actions';
import { ClientUser, EpsUser, PasswordResetResponse } from './users.interface';
import { catchError, debounceTime, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { tap } from 'rxjs';
import { NotificationService } from 'app/core/services/notification.service';
import { util } from 'app/core/util/util';
import { HttpErrorResponse } from '@angular/common/http';
import { RootState } from '../../index';

@Injectable({ providedIn: 'root' })
export class UsersEffects extends AbstractEffects {
  syncUsersWithAD$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.USERS_SYNC_WITH_AD),
      switchMap(() =>
        this.userRestService.syncWithAD().pipe(
          tap(() => {
           this.notificationService.showToastr({
             type: 'success',
             messageCode: 'SHARED.SYNC_SUCCESS',
             disableClose: false,
           });
          }),
          switchMap(() => {
            return [
              new actions.UsersSyncWithADSuccess(),
            ];
          }),
          catchError(err => {
            this.notificationService.showModal({
              type: 'error',
              message: 'Error syncing users with Active Directory',
              disableClose: false,
            });
            return concat(
              of(new actions.UsersSyncWithADFail(err))
            );
          })
        )
      )
    )
  );

  loadEpsUser$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<EpsUserLoadAction>(actions.EPS_USER_LOAD),
      switchMap((action: EpsUserLoadAction) =>
        this.userRestService.getEpsUserDetail(action.payload).pipe(
          map(user => new actions.EpsUserLoadSuccessAction(user)),
          catchError(err => {
            return this.handleFail(err, 'ADMIN.USERS.LIST.FIND_USER_TITLE', [of(new actions.EpsUserLoadFailAction(err))]);
          })
        )
      )
    )
  );
  loadClientUser$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<ClientUserLoadAction>(actions.CLIENT_USER_LOAD),
      switchMap((action: ClientUserLoadAction) => {
        return this.userRestService.getClientUserDetail(action.payload).pipe(
          map(user => new actions.ClientUserLoadSuccessAction(user)),
          catchError(err => {
            return this.handleFail(err, 'ADMIN.USERS.LIST.FIND_USER_TITLE', [of(new actions.ClientUserLoadFailAction(err))]);
          })
        );
      })
    )
  );
  loadEpsUsers$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<EpsUsersLoadAction>(actions.EPS_USERS_LOAD),
      debounceTime(50),
      switchMap((action: EpsUsersLoadAction) =>
        this.userRestService
          .getEpsUsers(action.payload.filter, action.payload.pageSize, action.payload.pageNr, action.payload.sortField, action.payload.sortAscending) // Request orders
          .pipe(
            map(
              response =>
                new actions.EpsUsersLoadSuccessAction(
                  Object.assign({}, response, {
                    items: response.items as EpsUser[],
                    adminDefinitions: action.payload.adminDefinitions,
                    pageNr: action.payload.pageNr as number,
                    pageSize: action.payload.pageSize as number,
                    sortField: action.payload.sortField,
                    sortAscending: action.payload.sortAscending,
                  })
                )
            ),
            catchError(err => {
              return this.handleFail(err, 'ADMIN.USERS.LIST.FIND_USERS_TITLE', [of(new actions.EpsUsersLoadFailAction(err))]);
            })
          )
      )
    )
  );
  loadClientUsers$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<ClientUsersLoadAction>(actions.CLIENT_USERS_LOAD),
      debounceTime(50),
      switchMap((action: ClientUsersLoadAction) =>
        this.userRestService
          .getClientUsers(action.payload.filter, action.payload.pageSize, action.payload.pageNr, action.payload.sortField, action.payload.sortAscending) // Request orders
          .pipe(
            map(response => {
              return new actions.ClientUsersLoadSuccessAction(
                Object.assign({}, response, {
                  items: response.items as ClientUser[],
                  adminDefinitions: action.payload.adminDefinitions,
                  pageNr: action.payload.pageNr as number,
                  pageSize: action.payload.pageSize as number,
                  sortField: action.payload.sortField,
                  sortAscending: action.payload.sortAscending,
                })
              );
            }),
            catchError((err: HttpErrorResponse) => {
              return this.handleFail(err, 'ADMIN.USERS.LIST.FIND_USERS_TITLE', [of(new actions.ClientUsersLoadFailAction(err))]);
            })
          )
      )
    )
  );
  updateEpsUser$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<EpsUserUpdateAction>(actions.EPS_USER_UPDATE),
      withLatestFrom(this.store, (action, state) => {
        return { action, salesOrganisations: state.admin.adminDefinitions.salesOrganisation }
      }),
      switchMap(({action, salesOrganisations}) =>
        this.userRestService.updateEpsUser(action.payload, salesOrganisations).pipe(
          switchMap(response => {
            this.notificationService.showModal({
              type: 'success',
              titleCode: 'SUCCESS',
              messageCode: 'ADMIN.SHARED.USER_UPDATE_SUCCESS',
              redirectUrl: '/admin/epsUsers',
              buttonText: 'SHARED.BUTTONS.GO_TO_OVERVIEW',
              disableClose: false,
            });
            return from([new actions.EpsUserUpdateSuccessAction(response)]);
          }),
          catchError(err => {
            return this.handleFail(err, 'ADMIN.SHARED.USER_UPDATE_FAIL', [of(new actions.EpsUserUpdateFailAction(err))]);
          })
        )
      )
    )
  );
  updateClientUser$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<ClientUserUpdateAction>(actions.CLIENT_USER_UPDATE),
      switchMap((action: ClientUserUpdateAction) =>
        this.userRestService.updateClientUser(action.payload).pipe(
          switchMap(response => {
            this.notificationService.showModal({
              type: 'success',
              titleCode: 'SUCCESS',
              messageCode: 'ADMIN.SHARED.USER_UPDATE_SUCCESS',
              redirectUrl: '/admin/clientUsers',
              buttonText: 'SHARED.BUTTONS.GO_TO_OVERVIEW',
              disableClose: false,
            });
            return from([new actions.ClientUserUpdateSuccessAction(response)]);
          }),
          catchError(err => {
            return this.handleFail(err, 'ADMIN.SHARED.USER_UPDATE_FAIL', [of(new actions.ClientUserUpdateFailAction(err))]);
          })
        )
      )
    )
  );
  inviteClientUser$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<ClientUserInviteAction>(actions.CLIENT_USER_INVITE_UPDATE),
      switchMap((action: ClientUserInviteAction) =>
        this.userRestService.inviteClientUser(action.payload).pipe(
          switchMap(response => {
            this.notificationService.showModal({
              type: 'success',
              titleCode: 'SUCCESS',
              messageCode: 'ADMIN.SHARED.USER_INVITE_SUCCESS',
              redirectUrl: '/admin/clientUsers',
              buttonText: 'SHARED.BUTTONS.GO_TO_OVERVIEW',
              disableClose: false,
            });
            return from([new actions.ClientUserInviteSuccessAction(response)]);
          }),
          catchError(err => {
            return this.handleFail(err, 'ADMIN.SHARED.USER_INVITE_FAIL', [of(new actions.ClientUserInviteFailAction(err))]);
          })
        )
      )
    )
  );
  inviteEpsUser$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<EpsUserInviteAction>(actions.EPS_USER_INVITE_UPDATE),
      withLatestFrom(this.store, (action, state) => {
        return { action, salesOrganisations: state.admin.adminDefinitions.salesOrganisation }
      }),
      switchMap(({action, salesOrganisations}) =>
        this.userRestService.inviteEpsUser(action.payload, salesOrganisations).pipe(
          switchMap(response => {
            this.notificationService.showModal({
              type: 'success',
              titleCode: 'SUCCESS',
              messageCode: 'ADMIN.SHARED.USER_INVITE_SUCCESS',
              redirectUrl: '/admin/epsUsers',
              buttonText: 'SHARED.BUTTONS.GO_TO_OVERVIEW',
              disableClose: false,
            });
            return from([new actions.EpsUserInviteSuccessAction(response)]);
          }),
          catchError(err => {
            return this.handleFail(err, 'ADMIN.SHARED.USER_INVITE_FAIL', [of(new actions.EpsUserInviteFailAction(err))]);
          })
        )
      )
    )
  );
  createClientUser$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<ClientUserCreateAction>(actions.CLIENT_USER_CREATE),
      switchMap((action: ClientUserCreateAction) =>
        this.userRestService.createClientUser(action.payload).pipe(
          switchMap((response: any) => {
            this.notificationService.showModal({
              type: 'success',
              titleCode: 'SUCCESS',
              messageCode: 'ADMIN.SHARED.USER_CREATE_SUCCESS',
              redirectUrl: '/admin/clientUsers',
              buttonText: 'SHARED.BUTTONS.GO_TO_OVERVIEW',
              disableClose: false,
              translationParams: response,
              copyToClipboard: `${response.mail} ${response.password}`,
            });
            return from([new actions.ClientUserCreateSuccessAction(response)]);
          }),
          catchError(err => {
            return this.handleFail(err, 'ADMIN.SHARED.USER_CREATE_FAIL', [of(new actions.ClientUserCreateFailAction(err))]);
          })
        )
      )
    )
  );
  createEpsUser$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<EpsUserCreateAction>(actions.EPS_USER_CREATE),
      withLatestFrom(this.store, (action, state) => {
        return { action, salesOrganisations: state.admin.adminDefinitions.salesOrganisation }
      }),
      switchMap(({action, salesOrganisations}) =>
        this.userRestService.createEpsUser(action.payload, salesOrganisations).pipe(
          switchMap((response: any) => {
            this.notificationService.showModal({
              type: 'success',
              titleCode: 'SUCCESS',
              messageCode: 'ADMIN.SHARED.USER_CREATE_SUCCESS',
              redirectUrl: '/admin/epsUsers',
              buttonText: 'SHARED.BUTTONS.GO_TO_OVERVIEW',
              disableClose: false,
              translationParams: response,
              copyToClipboard: `${response.mail} ${response.password}`,
            });
            return from([new actions.EpsUserCreateSuccessAction(response)]);
          }),
          catchError(err => {
            return this.handleFail(err, 'ADMIN.SHARED.USER_CREATE_FAIL', [of(new actions.EpsUserCreateFailAction(err))]);
          })
        )
      )
    )
  );
  getSoldTosForSalesOrganisation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<ClientUserLoadSoldTosAction>(actions.CLIENT_USER_LOAD_SOLDTOS),
      switchMap((action: ClientUserLoadSoldTosAction) =>
        from(this.customersRestService.getSoldTosForSalesOrganisation(action.payload.salesOrganisationId)).pipe(
          map(userContext => {
            return new actions.ClientUserLoadSoldTosSuccessAction({
              soldTos: userContext.items.map(c => {
                return {
                  id: c.customerNumber,
                  name: c.customerName,
                };
              }),
            });
          }),
          catchError(err => {
            return this.handleFail(err, 'Get sold-tos', [of(new actions.ClientUserLoadSoldTosFailAction(err))]);
          })
        )
      )
    )
  );
  getShipTosForSoldTo$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<ClientUserLoadShipTosAction>(actions.CLIENT_USER_LOAD_SHIPTOS),
      switchMap((action: ClientUserLoadShipTosAction) =>
        from(this.customersRestService.getShipTosForSoldTo(action.payload.soldToId)).pipe(
          map(userContext => {
            return new actions.ClientUserLoadShipTosSuccessAction({
              shipTos: userContext.items.map(c => {
                return {
                  id: c.customerNumber,
                  name: c.customerName,
                };
              }),
            });
          }),
          catchError(err => {
            return this.handleFail(err, 'Get ship-tos', [of(new actions.ClientUserLoadShipTosFailAction(err))]);
          })
        )
      )
    )
  );
  downloadBulkClientUserSheet: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.CLIENT_USER_BULK_DOWNLOAD_EXAMPLE),
      debounceTime(50),
      switchMap(() =>
        this.userRestService.downloadExampleBulkClientUsersSheet().pipe(
          map((blob: Blob) => {
            return new actions.ClientUserBulkDownloadExampleSuccessAction({ filename: `users.${util.getExtension(blob.type)}`, blob });
          }),
          catchError(err => this.handleFail(err, 'FLOWS.NOTIFICATIONS.DOWNLOAD_FILE', [of(new actions.ClientUserBulkDownloadExampleFailAction(err))]))
        )
      )
    )
  );
  uploadBulkClientUserSheet: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<ClientUserBulkUploadAction>(actions.CLIENT_USER_BULK_UPLOAD),
      debounceTime(50),
      switchMap((action: ClientUserBulkUploadAction) =>
        this.userRestService.createInviteBulkClientUsersSheetUpload(action.payload).pipe(
          map((response: Response) => {
            debugger; // TODO BFR: test, implement
            return new actions.ClientUserBulkUploadResponseAction({ filename: 'client-user-invitations.xlsx', blob: null });
          }),
          catchError(err => this.handleFail(err, 'FLOWS.NOTIFICATIONS.DOWNLOAD_FILE', [of(new actions.ClientUserBulkUploadFailAction(err))]))
        )
      )
    )
  );
  reInviteUser$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<UserReInviteAction>(actions.USER_RE_INVITE),
      switchMap((action: UserReInviteAction) =>
        this.userRestService.reInviteUser(action.payload.userId).pipe(
          switchMap(response => {
            this.notificationService.showModal({
              type: 'success',
              titleCode: 'SUCCESS',
              messageCode: 'ADMIN.SHARED.USER_RE_INVITE_SUCCESS',
              redirectUrl: action.payload.overviewUrl,
              buttonText: 'SHARED.BUTTONS.GO_TO_OVERVIEW',
              disableClose: false,
              translationParams: response,
            });
            return from([new actions.UserReInviteSuccessAction()]);
          }),

          catchError(err => {
            return this.handleFail(err, 'ADMIN.SHARED.USER_RE_INVITE_FAIL', [of(new actions.UserReInviteFailAction(err))]);
          })
        )
      )
    )
  );
  resetPasswordUser$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<UserResetPasswordAction>(actions.USER_RESET_PASSWORD),
      switchMap((action: UserResetPasswordAction) =>
        from(this.userRestService.resetPasswordUser(action.payload.userId)).pipe(
          switchMap((response: PasswordResetResponse) => {
            if (response.success) {
              this.notificationService.showModal({
                type: 'success',
                titleCode: 'SUCCESS',
                messageCode: 'ADMIN.SHARED.USER_RESET_PASSWORD_SUCCESS',
                redirectUrl: action.payload.overviewUrl,
                buttonText: 'SHARED.BUTTONS.GO_TO_OVERVIEW',
                disableClose: false,
                translationParams: response,
                copyToClipboard: response.newPassword,
              });
              return from([new actions.UserResetPasswordSuccessAction()]);
            } else {
              return this.handleFail(null, 'ADMIN.SHARED.USER_RESET_PASSWORD_FAIL', [of(new actions.UserResetPasswordFailAction(null))]);
            }
          }),
          catchError(err => {
            return this.handleFail(err, 'ADMIN.SHARED.USER_RESET_PASSWORD_FAIL', [of(new actions.UserResetPasswordFailAction(err))]);
          })
        )
      )
    )
  );

  constructor(
    private store: Store<RootState>,
    translateService: TranslateService,
    azureMonitoringService: AzureMonitoringService,
    private actions$: Actions,
    private userRestService: UserRestService,
    private customersRestService: CustomersRestService,
    notificationService: NotificationService
  ) {
    super(translateService, azureMonitoringService, notificationService);
  }
}
